import { toast } from 'sonner';
import {
  AMC_PAYMENT_SOURCES,
  matchPaymentLink,
  safeHttpsUrl,
  type AmcPaymentSource
} from '../../../../../supabase/functions/_shared/appraisal-payment-links';
import type { AppraisalPaymentReference } from '../../../../../supabase/functions/_shared/appraisal-workspace-contract';
import type { AddPaymentLinkInput } from '@/services/appraisalWorkspaceService';

/** An AMC staff can choose in Add payment link, from the same list the capture paths trust. */
export interface PaymentLinkAmc {
  name: string;
  source: AmcPaymentSource;
  /** The one page every order uses, when the AMC has a generic page: the borrower also needs both IDs. */
  genericPage: string | null;
  /** Where its payment links are, for the field hint. */
  hosts: string[];
}

export const OTHER_AMC = 'Other';

export const PAYMENT_LINK_AMCS: PaymentLinkAmc[] = AMC_PAYMENT_SOURCES.map((source) => {
  const generic = source.links.find((link) => link.generic);
  return {
    name: source.name,
    source,
    // A generic page's path prefix is the page itself.
    genericPage: generic ? `https://${generic.host}${generic.pathPrefix}` : null,
    hosts: [...new Set(source.links.map((link) => link.host))]
  };
});

export interface PaymentLinkDraft {
  /** An AMC's name, OTHER_AMC, or '' before a choice. */
  amc: string;
  url: string;
  amcName: string;
  fileId: string;
  orderReferenceId: string;
  amount: string;
}

export type PaymentLinkField = 'amc' | 'amcName' | 'url' | 'fileId' | 'orderReferenceId';
/** The order fields appear in, so focus moves to the first one to fix. */
export const PAYMENT_LINK_FIELDS: PaymentLinkField[] = ['amc', 'amcName', 'url', 'fileId', 'orderReferenceId'];

export type PaymentLinkCheck =
  | { ok: true; input: Omit<AddPaymentLinkInput, 'observationId' | 'replaceRequestId'> }
  | { ok: false; errors: Partial<Record<PaymentLinkField, string>> };

// The IDs as an AMC prints them; a pasted label such as "File ID: 123" is not an ID.
const ORDER_ID = /^[A-Za-z0-9-]{3,40}$/;

function idError(value: string, label: string, amc: string): string | undefined {
  if (!value) {
    return `Enter the ${label} from ${amc}’s email.`;
  }
  return ORDER_ID.test(value) ? undefined : `Enter only the ${label} as ${amc}’s email shows it: letters, numbers and dashes.`;
}

function linkError(url: string, amc: string): string | undefined {
  if (!url) {
    return `Paste the payment link from ${amc}’s email.`;
  }
  return safeHttpsUrl(url) ? undefined : 'Paste the full link, starting with https://.';
}

/** Checks a draft the way the server will, plus the AMC's own page, and builds the server input. */
export function checkPaymentLinkDraft(draft: PaymentLinkDraft): PaymentLinkCheck {
  const url = draft.url.trim();
  const amount = draft.amount.trim() || undefined;
  const fileId = draft.fileId.trim();
  const orderReferenceId = draft.orderReferenceId.trim();
  const errors: Partial<Record<PaymentLinkField, string>> = {};
  const amc = PAYMENT_LINK_AMCS.find((choice) => choice.name === draft.amc);
  if (amc?.genericPage) {
    errors.fileId = idError(fileId, 'File ID', amc.name);
    errors.orderReferenceId = idError(orderReferenceId, 'Order Reference ID', amc.name);
  } else if (amc) {
    errors.url =
      linkError(url, amc.name) ??
      (matchPaymentLink(url, [amc.source]) ? undefined : `This isn’t a ${amc.name} payment link. Check the link, or choose Other.`);
  } else if (draft.amc === OTHER_AMC) {
    const listed = matchPaymentLink(url);
    errors.amcName = draft.amcName.trim() ? undefined : 'Enter the AMC’s name.';
    errors.url =
      linkError(url, 'the AMC') ?? (listed ? `This is a ${listed.amcName} payment link. Choose ${listed.amcName} above.` : undefined);
  } else {
    errors.amc = 'Choose the AMC that sent the link.';
  }
  const found = Object.fromEntries(Object.entries(errors).filter(([, message]) => message)) as Partial<Record<PaymentLinkField, string>>;
  if (Object.keys(found).length) {
    return { ok: false, errors: found };
  }
  if (amc?.genericPage) {
    return { ok: true, input: { url: amc.genericPage, amcName: amc.name, fileId, orderReferenceId, amount } };
  }
  if (amc) {
    return { ok: true, input: { url, amcName: amc.name, amount } };
  }
  return {
    ok: true,
    input: { url, amcName: draft.amcName.trim(), fileId: fileId || undefined, orderReferenceId: orderReferenceId || undefined, amount }
  };
}

/** The link, then each stored ID on its own line: a generic page also asks for them. */
export function paymentLinkClipboard(link: {
  url: string;
  reference?: Pick<AppraisalPaymentReference, 'fileId' | 'orderReferenceId'> | null;
}): string {
  return [
    link.url,
    link.reference?.fileId ? `File ID: ${link.reference.fileId}` : null,
    link.reference?.orderReferenceId ? `Order Reference ID: ${link.reference.orderReferenceId}` : null
  ]
    .filter(Boolean)
    .join('\n');
}

export async function copyPaymentLink(link: Parameters<typeof paymentLinkClipboard>[0]): Promise<void> {
  const text = paymentLinkClipboard(link);
  try {
    await navigator.clipboard.writeText(text);
    toast.success(text === link.url ? 'Payment link copied.' : 'Payment link and IDs copied.');
  } catch {
    toast.error('The link could not be copied. Open the AMC email instead.');
  }
}
